/* @refresh reset */
/**
 * useAppStateSession Hook
 * Manages session-specific state that persists in localStorage
 * Used for UI state like shouldShowCall (video call panel visibility)
 */

import React, { Dispatch, SetStateAction, useContext, useState, useEffect } from 'react'

export type SessionState = {
  shouldShowCall?: boolean
}

// eslint-disable-next-line react-refresh/only-export-components
export const DEFAULT_SESSION_STATE: SessionState = {
  shouldShowCall: false
}

type SessionContextType = {
  state: SessionState
  setState: Dispatch<SetStateAction<SessionState>>
}

// eslint-disable-next-line react-refresh/only-export-components
export const SessionContext = React.createContext<SessionContextType>({
  state: DEFAULT_SESSION_STATE,
  setState: () => ({})
})

/**
 * Provider for session state
 */
export const SessionStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<SessionState>(() => {
    // Try to load from localStorage
    try {
      const stored = localStorage.getItem('session-state')
      if (stored) {
        return JSON.parse(stored)
      }
    } catch (error) {}
    return DEFAULT_SESSION_STATE
  })

  // Persist to localStorage whenever state changes
  useEffect(() => {
    try {
      localStorage.setItem('session-state', JSON.stringify(state))
    } catch (error) {}
  }, [state])

  return <SessionContext.Provider value={{ state, setState }}>{children}</SessionContext.Provider>
}

/**
 * Hook to access session state
 */
// eslint-disable-next-line react-refresh/only-export-components
export default function useAppStateSession() {
  return useContext(SessionContext)
}
